import type { ReactNode } from 'react';

import { Button, type ButtonVariant } from '@/components/common/button';

import styles from './overlay-chrome.module.scss';

export type OverlayFooterProps = {
  primaryText?: string;
  onPrimary?: () => void;
  /** When true the primary button is `type="submit"` (used by FormModal/FormDrawer). */
  isSubmit?: boolean;
  primaryVariant?: ButtonVariant;
  primaryDanger?: boolean;
  loading?: boolean;
  disabled?: boolean;
  cancelText?: string;
  onCancel?: () => void;
  /** Disables the cancel/secondary button independently of `loading` (e.g. a wizard's Previous on
   *  its first step, which renders disabled rather than hidden so the footer does not reflow). */
  cancelDisabled?: boolean;
  align?: 'end' | 'center';
  /** Optional left-aligned node (e.g. a switch-mode link), pushed to the far left of the footer. */
  footerLeft?: ReactNode;
};

export function OverlayFooter({
  primaryText,
  onPrimary,
  isSubmit = false,
  primaryVariant = 'primary',
  primaryDanger = false,
  loading = false,
  disabled = false,
  cancelText = 'Cancel',
  onCancel,
  cancelDisabled = false,
  align = 'end',
  footerLeft,
}: OverlayFooterProps) {
  const showCancel = Boolean(cancelText && onCancel);
  if (!primaryText && !showCancel && !footerLeft) return null;

  return (
    <div className={[styles.footer, align === 'center' ? styles.footerCenter : ''].join(' ')}>
      {footerLeft ? <div className={styles.footerLeft}>{footerLeft}</div> : null}
      {showCancel ? (
        <Button variant="secondary" onClick={onCancel} disabled={loading || cancelDisabled}>
          {cancelText}
        </Button>
      ) : null}
      {primaryText ? (
        <Button
          htmlType={isSubmit ? 'submit' : 'button'}
          variant={primaryVariant}
          onClick={isSubmit ? undefined : onPrimary}
          loading={loading}
          disabled={disabled}
          className={primaryDanger ? styles.danger : undefined}
        >
          {primaryText}
        </Button>
      ) : null}
    </div>
  );
}
